/**
 * Milestone & Phase Overrides — read/write helpers for the in-memory store.
 *
 * Follows the same pattern as scheduleProgressOverrides.ts:
 *   - Reads from store Maps (milestoneStore, phaseStore)
 *   - Writes to store + fires CustomEvent for React re-render
 *   - API write-through: mutations call the backend and update the store
 *     with the server-returned entity (canonical ID, timestamps)
 *   - Hydration: loadMilestones / loadPhases fetch from API and seed the store
 *
 * No localStorage reads or writes.
 */

import type { ProjectMilestone, ProjectPhase } from './milestoneTypes';
import { milestoneStore, phaseStore } from '../store';
import {
  fetchProjectMilestones, createProjectMilestone,
  updateProjectMilestone, deleteProjectMilestone,
} from '../../api/projectMilestones';
import type { ProjectMilestoneResponse } from '../../api/projectMilestones';
import {
  fetchProjectPhases, createProjectPhase,
  updateProjectPhase, deleteProjectPhase,
} from '../../api/projectPhases';
import type { ProjectPhaseResponse } from '../../api/projectPhases';

const EVENT_NAME = 'l4e_milestones_changed';

function projectKey(projectGuid: string): string {
  return String(projectGuid || '').trim().toUpperCase();
}

function notify(): void {
  try {
    window.dispatchEvent(new CustomEvent(EVENT_NAME));
  } catch { /* SSR guard */ }
}

// ── Shape converters (API response → store type) ────────────

function apiToMilestone(r: ProjectMilestoneResponse): ProjectMilestone {
  return {
    milestone_guid: r.id,
    project_guid: r.projectId,
    name: r.name,
    target_date: r.targetDate,
    actual_date: r.actualDate,
    sort_order: r.sortOrder,
    source: (r.source as 'manual' | 'gantt') || 'manual',
    gantt_task_id: r.ganttTaskId ? Number(r.ganttTaskId) || null : null,
  };
}

function apiToPhase(r: ProjectPhaseResponse): ProjectPhase {
  return {
    phase_guid: r.id,
    project_guid: r.projectId,
    name: r.name,
    start_date: r.startDate,
    end_date: r.endDate,
    percent_complete: r.percentComplete,
    sort_order: r.sortOrder,
    source: (r.source as 'manual' | 'gantt') || 'manual',
    gantt_task_id: r.ganttTaskId ? Number(r.ganttTaskId) || null : null,
  };
}

// ── Hydration (fetch from API → seed store) ─────────────────

/** Track which projects have been loaded to avoid redundant fetches. */
const _loadedMilestones = new Set<string>();
const _loadedPhases = new Set<string>();

/**
 * Fetch milestones from the API and populate the store.
 * Safe to call multiple times — only fetches once per project per session.
 */
export async function loadMilestones(projectGuid: string): Promise<void> {
  const k = projectKey(projectGuid);
  if (!k || _loadedMilestones.has(k)) return;
  try {
    const data = await fetchProjectMilestones(projectGuid);
    milestoneStore.set(k, data.map(apiToMilestone));
    _loadedMilestones.add(k);
    notify();
  } catch (err) {
    console.warn('[milestoneOverrides] Failed to load milestones:', err);
  }
}

/**
 * Fetch phases from the API and populate the store.
 * Safe to call multiple times — only fetches once per project per session.
 */
export async function loadPhases(projectGuid: string): Promise<void> {
  const k = projectKey(projectGuid);
  if (!k || _loadedPhases.has(k)) return;
  try {
    const data = await fetchProjectPhases(projectGuid);
    phaseStore.set(k, data.map(apiToPhase));
    _loadedPhases.add(k);
    notify();
  } catch (err) {
    console.warn('[milestoneOverrides] Failed to load phases:', err);
  }
}

// ── Milestone reads ─────────────────────────────────────────

export function getMilestones(projectGuid: string): ProjectMilestone[] {
  return milestoneStore.get(projectKey(projectGuid)) ?? [];
}

// ── Milestone writes (store + API write-through) ────────────

export function addMilestone(projectGuid: string, milestone: ProjectMilestone): void {
  const k = projectKey(projectGuid);
  const list = [...(milestoneStore.get(k) ?? []), milestone];
  list.sort((a, b) => a.sort_order - b.sort_order);
  milestoneStore.set(k, list);
  notify();

  // Fire-and-forget API write; replace client-generated ID with server ID on success
  createProjectMilestone(projectGuid, {
    name: milestone.name,
    targetDate: milestone.target_date,
    actualDate: milestone.actual_date,
    sortOrder: milestone.sort_order,
  }).then((resp) => {
    // Replace the optimistic entry with server-canonical data
    const current = milestoneStore.get(k) ?? [];
    const idx = current.findIndex((m) => m.milestone_guid === milestone.milestone_guid);
    if (idx !== -1) {
      current[idx] = apiToMilestone(resp);
      milestoneStore.set(k, [...current]);
      notify();
    }
  }).catch((err) => {
    console.error('[milestoneOverrides] API create failed:', err);
  });
}

export function updateMilestone(projectGuid: string, milestoneGuid: string, patch: Partial<ProjectMilestone>): void {
  const k = projectKey(projectGuid);
  const list = milestoneStore.get(k) ?? [];
  const idx = list.findIndex((m) => m.milestone_guid === milestoneGuid);
  if (idx === -1) return;
  list[idx] = { ...list[idx], ...patch };
  milestoneStore.set(k, [...list]);
  notify();

  // API write-through
  updateProjectMilestone(milestoneGuid, {
    name: patch.name,
    targetDate: patch.target_date,
    actualDate: patch.actual_date,
    sortOrder: patch.sort_order,
    clearActualDate: patch.actual_date === null && 'actual_date' in patch,
  }).catch((err) => {
    console.error('[milestoneOverrides] API update failed:', err);
  });
}

export function deleteMilestone(projectGuid: string, milestoneGuid: string): void {
  const k = projectKey(projectGuid);
  const list = (milestoneStore.get(k) ?? []).filter((m) => m.milestone_guid !== milestoneGuid);
  milestoneStore.set(k, list);
  notify();

  // API write-through
  deleteProjectMilestone(milestoneGuid).catch((err) => {
    console.error('[milestoneOverrides] API delete failed:', err);
  });
}

// ── Phase reads ─────────────────────────────────────────────

export function getPhases(projectGuid: string): ProjectPhase[] {
  return phaseStore.get(projectKey(projectGuid)) ?? [];
}

// ── Phase writes (store + API write-through) ────────────────

export function addPhase(projectGuid: string, phase: ProjectPhase): void {
  const k = projectKey(projectGuid);
  const list = [...(phaseStore.get(k) ?? []), phase];
  list.sort((a, b) => a.sort_order - b.sort_order);
  phaseStore.set(k, list);
  notify();

  // Fire-and-forget API write; replace client-generated ID with server ID on success
  createProjectPhase(projectGuid, {
    name: phase.name,
    startDate: phase.start_date,
    endDate: phase.end_date,
    percentComplete: phase.percent_complete,
    sortOrder: phase.sort_order,
  }).then((resp) => {
    const current = phaseStore.get(k) ?? [];
    const idx = current.findIndex((p) => p.phase_guid === phase.phase_guid);
    if (idx !== -1) {
      current[idx] = apiToPhase(resp);
      phaseStore.set(k, [...current]);
      notify();
    }
  }).catch((err) => {
    console.error('[milestoneOverrides] API create phase failed:', err);
  });
}

export function updatePhase(projectGuid: string, phaseGuid: string, patch: Partial<ProjectPhase>): void {
  const k = projectKey(projectGuid);
  const list = phaseStore.get(k) ?? [];
  const idx = list.findIndex((p) => p.phase_guid === phaseGuid);
  if (idx === -1) return;
  list[idx] = { ...list[idx], ...patch };
  phaseStore.set(k, [...list]);
  notify();

  // API write-through
  updateProjectPhase(phaseGuid, {
    name: patch.name,
    startDate: patch.start_date,
    endDate: patch.end_date,
    percentComplete: patch.percent_complete,
    sortOrder: patch.sort_order,
  }).catch((err) => {
    console.error('[milestoneOverrides] API update phase failed:', err);
  });
}

export function deletePhase(projectGuid: string, phaseGuid: string): void {
  const k = projectKey(projectGuid);
  const list = (phaseStore.get(k) ?? []).filter((p) => p.phase_guid !== phaseGuid);
  phaseStore.set(k, list);
  notify();

  // API write-through
  deleteProjectPhase(phaseGuid).catch((err) => {
    console.error('[milestoneOverrides] API delete phase failed:', err);
  });
}

// ── Event name export (for useEffect listeners) ─────────────

export const MILESTONES_CHANGED_EVENT = EVENT_NAME;
